import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, DollarSign, ClipboardCheck, Calendar, Headphones, ShoppingCart } from 'lucide-react';
import { TIMEPAYMENT_URL } from '../lib/api';

const PHONE_DISPLAY = '(984) 201-0767';
const PHONE_TEL = '+19842010767';

const trackClick = (eventName, params = {}) => {
    if (typeof window !== 'undefined' && window.trackConversion) {
        window.trackConversion(eventName, params);
    }
};

// Helper to build /contact URL with prefilled context
const buildContactHref = ({ subject, service, machine, interest }) => {
    const p = new URLSearchParams();
    if (subject) p.set('subject', subject);
    if (service) p.set('service', service);
    if (machine) p.set('machine', machine);
    if (interest) p.set('interest', interest);
    const qs = p.toString();
    return qs ? `/contact?${qs}` : '/contact';
};

/**
 * CTASection — reusable conversion block for the bottom of every page.
 *
 * Variants:
 *   services / project / generic → Shop Equipment / Get a Quote / Rent & Train
 *   machine      → Request Equipment Quote / Finance This Machine / Talk to an Equipment Specialist
 *   knowledge    → Talk to a Specialist / Call Now
 *
 * Each CTA fires a distinct GA4 event so campaigns can be measured separately:
 *   shop_click, rental_click, phone_click, equipment_quote_click,
 *   finance_click, specialist_click
 */
const CTASection = ({
    variant = 'generic',
    eyebrow,
    heading,
    subheading,
    context = {},
    className = '',
    testid = 'cta-section',
}) => {
    const machineName = context.machine || '';
    const serviceKey = context.service || '';
    const trackingLocation = context.trackingLocation || variant;

    // Primary + secondary + tertiary CTA config per variant
    let ctas = [];

    if (variant === 'machine') {
        ctas = [
            {
                to: buildContactHref({
                    subject: machineName ? `Equipment quote request: ${machineName}` : 'Equipment quote request',
                    machine: machineName,
                    interest: 'quote',
                }),
                label: 'Request Equipment Quote',
                icon: ClipboardCheck,
                primary: true,
                event: 'equipment_quote_click',
                testid: 'cta-equipment-quote',
            },
            {
                href: TIMEPAYMENT_URL,
                external: true,
                label: 'Finance This Machine',
                icon: DollarSign,
                event: 'finance_click',
                testid: 'cta-finance-machine',
            },
            {
                href: `tel:${PHONE_TEL}`,
                label: 'Talk to an Equipment Specialist',
                icon: Headphones,
                event: 'specialist_click',
                testid: 'cta-specialist',
            },
        ];
        if (context.priced) ctas = ctas.slice(1);
    } else if (variant === 'knowledge') {
        ctas = [
            {
                to: buildContactHref({
                    subject: context.subject || 'Question for a specialist',
                    interest: 'specialist',
                }),
                label: 'Talk to a Specialist',
                icon: Headphones,
                primary: true,
                event: 'specialist_click',
                testid: 'cta-specialist',
            },
            {
                href: `tel:${PHONE_TEL}`,
                label: `Call ${PHONE_DISPLAY}`,
                icon: Phone,
                event: 'phone_click',
                testid: 'cta-phone',
            },
        ];
    } else {
        // services / project / generic → equipment actions
        ctas = [
            {
                to: '/shop',
                label: 'Shop Equipment',
                icon: ShoppingCart,
                primary: true,
                event: 'shop_click',
                testid: 'cta-shop',
            },
            {
                to: buildContactHref({ subject: context.subject || 'Equipment quote request', service: serviceKey, interest: 'quote' }),
                label: 'Get a Quote',
                icon: ClipboardCheck,
                event: 'equipment_quote_click',
                testid: 'cta-quote',
            },
            {
                to: '/rental',
                label: 'Rent & Train',
                icon: Calendar,
                event: 'rental_click',
                testid: 'cta-rental',
            },
        ];
    }

    const _eyebrow = eyebrow || (variant === 'project' ? '/ Need similar results?' : '/ Ready to get started?');
    const _heading = heading || (
        variant === 'machine'
            ? 'Get pricing on this equipment.'
            : variant === 'project'
                ? 'Get results like these.'
                : variant === 'knowledge'
                    ? 'Talk to a real specialist.'
                    : 'Get the right equipment for the job.'
    );
    const _sub = subheading || (
        variant === 'machine'
            ? 'Talk to a specialist about specs, financing, and rentals.'
            : variant === 'project'
                ? 'The same dry ice blasting equipment is available to buy or rent — with training and support included.'
                : variant === 'knowledge'
                    ? 'We answer real engineering and maintenance questions every day.'
                    : 'Shop machines and parts, get a quote, or rent one and get trained first.'
    );

    return (
        <section
            className={`border-t border-neutral-200 bg-white ${className}`}
            data-testid={testid}
        >
            <div className="container-page py-16 md:py-20">
                <div className="max-w-3xl">
                    <div className="label-caps text-brand-primary mb-3 text-xs">{_eyebrow}</div>
                    <h2 className="font-display font-black uppercase text-3xl md:text-5xl text-neutral-900 leading-[0.95]">
                        {_heading}
                    </h2>
                    <p className="mt-5 text-base md:text-lg text-neutral-600 leading-relaxed">
                        {_sub}
                    </p>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                    {ctas.map((cta, i) => {
                        const Icon = cta.icon;
                        const baseClass = cta.primary
                            ? 'bg-brand-primary hover:bg-brand-primaryHover text-white'
                            : i === 1
                                ? 'border border-neutral-300 hover:border-neutral-900 text-neutral-900 bg-white'
                                : 'border border-neutral-300 hover:border-brand-primary hover:text-brand-primary text-neutral-800 bg-white';
                        const cls = `inline-flex items-center gap-2 ${baseClass} font-bold px-6 py-3.5 label-caps transition-colors`;
                        const onClick = () => trackClick(cta.event, { location: trackingLocation, variant });
                        const inner = (
                            <>
                                {Icon ? <Icon className="h-4 w-4" /> : null}
                                {cta.label}
                                {cta.primary ? <ArrowRight className="h-4 w-4" /> : null}
                            </>
                        );
                        if (cta.href) {
                            return (
                                <a
                                    key={i}
                                    href={cta.href}
                                    target={cta.external ? '_blank' : undefined}
                                    rel={cta.external ? 'noreferrer' : undefined}
                                    onClick={onClick}
                                    className={cls}
                                    data-testid={cta.testid}
                                >
                                    {inner}
                                </a>
                            );
                        }
                        return (
                            <Link
                                key={i}
                                to={cta.to}
                                onClick={onClick}
                                className={cls}
                                data-testid={cta.testid}
                            >
                                {inner}
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default CTASection;
export { PHONE_DISPLAY, PHONE_TEL, buildContactHref };
